import { useState, useEffect } from "react";
import {
  collection, doc, getDocs, addDoc, updateDoc,
  deleteDoc, setDoc, getDoc, serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase/config";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg: #0E1117; --surface: #161B27; --surface2: #1C2333; --surface3: #212A3A;
    --border: #252D3D; --border-light: #2E3A4E; --accent: #F5A623; --accent-dim: rgba(245,166,35,0.10);
    --blue: #3B82F6; --blue-dim: rgba(59,130,246,0.10); --green: #22C55E; --green-dim: rgba(34,197,94,0.10);
    --red: #EF4444; --red-dim: rgba(239,68,68,0.12); --purple: #A855F7; --purple-dim: rgba(168,85,247,0.10);
    --text: #F1F5F9; --text-sub: #94A3B8; --text-muted: #475569;
  }
  body { background: var(--bg); color: var(--text); font-family: 'DM Sans', sans-serif; min-height: 100vh; }
  .header { display: flex; align-items: center; justify-content: space-between; padding: 0 32px; height: 64px; background: rgba(22,27,39,0.97); backdrop-filter: blur(12px); border-bottom: 1px solid var(--border); position: sticky; top: 0; z-index: 100; }
  .logo { display: flex; align-items: center; gap: 10px; }
  .logo-icon { width: 32px; height: 32px; background: var(--accent); border-radius: 6px; display: flex; align-items: center; justify-content: center; font-family: 'Bebas Neue', sans-serif; font-size: 18px; color: #000; }
  .logo-text { font-family: 'Bebas Neue', sans-serif; font-size: 24px; letter-spacing: 3px; }
  .logo-text span { color: var(--accent); }
  .header-badge { background: var(--blue-dim); border: 1px solid rgba(59,130,246,0.3); border-radius: 20px; padding: 4px 12px; font-size: 11px; font-weight: 700; color: var(--blue); letter-spacing: 1px; text-transform: uppercase; }
  .header-right { display: flex; align-items: center; gap: 14px; }
  .header-store { font-size: 12px; color: var(--text-muted); }
  .header-store strong { color: var(--text-sub); }
  .avatar { width: 34px; height: 34px; border-radius: 50%; background: var(--blue-dim); border: 1.5px solid var(--blue); display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; color: var(--blue); }
  .layout { display: grid; grid-template-columns: 220px 1fr; min-height: calc(100vh - 64px); }
  .sidebar { background: var(--surface); border-right: 1px solid var(--border); padding: 24px 0; }
  .sidebar-label { font-size: 10px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--text-muted); padding: 0 20px; margin: 16px 0 8px; }
  .nav-item { display: flex; align-items: center; gap: 10px; padding: 9px 20px; font-size: 13px; font-weight: 500; color: var(--text-sub); cursor: pointer; transition: all 0.15s; border-left: 3px solid transparent; }
  .nav-item:hover { background: var(--surface2); color: var(--text); }
  .nav-item.active { background: var(--blue-dim); color: var(--blue); border-left-color: var(--blue); font-weight: 600; }
  .nav-icon { font-size: 15px; width: 20px; text-align: center; }
  .nav-badge { margin-left: auto; background: var(--accent); color: #000; border-radius: 10px; padding: 1px 7px; font-size: 10px; font-weight: 700; }
  .nav-badge.complete { background: var(--green); color: #000; }
  .main { padding: 36px; background: var(--bg); background-image: radial-gradient(ellipse at 80% 0%, rgba(59,130,246,0.03) 0%, transparent 60%); overflow-y: auto; }
  .page-head { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 28px; }
  .page-head-left h1 { font-family: 'Bebas Neue', sans-serif; font-size: 32px; letter-spacing: 3px; line-height: 1; }
  .page-head-left p { font-size: 13px; color: var(--text-muted); margin-top: 5px; }
  .page-head-actions { display: flex; gap: 10px; align-items: center; }
  .stat-strip { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 28px; }
  .stat-card { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 18px 20px; }
  .stat-card.highlight { border-color: rgba(59,130,246,0.3); }
  .stat-card.complete { border-color: rgba(34,197,94,0.3); }
  .stat-label { font-size: 11px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 8px; }
  .stat-value { font-family: 'Bebas Neue', sans-serif; font-size: 34px; letter-spacing: 1px; line-height: 1; }
  .stat-value.blue { color: var(--blue); } .stat-value.green { color: var(--green); } .stat-value.amber { color: var(--accent); } .stat-value.red { color: var(--red); }
  .stat-sub { font-size: 11px; color: var(--text-muted); margin-top: 4px; }
  .section-bar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
  .section-title { font-size: 11px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--text-muted); display: flex; align-items: center; gap: 8px; }
  .section-title::before { content: ''; display: block; width: 3px; height: 12px; background: var(--blue); border-radius: 2px; }
  .btn { padding: 8px 16px; border-radius: 7px; font-size: 12px; font-weight: 700; cursor: pointer; font-family: 'DM Sans', sans-serif; letter-spacing: 0.3px; transition: all 0.15s; border: none; display: inline-flex; align-items: center; gap: 6px; }
  .btn-primary { background: var(--accent); color: #000; } .btn-primary:hover { opacity: 0.88; }
  .btn-blue { background: var(--blue); color: #fff; } .btn-blue:hover { opacity: 0.88; }
  .btn-ghost { background: transparent; border: 1px solid var(--border); color: var(--text-sub); } .btn-ghost:hover { border-color: var(--text-sub); color: var(--text); }
  .btn-danger { background: var(--red-dim); border: 1px solid rgba(239,68,68,0.3); color: var(--red); }
  .btn-green { background: var(--green-dim); border: 1px solid rgba(34,197,94,0.3); color: var(--green); }
  .btn-sm { padding: 5px 10px; font-size: 11px; } .btn-xs { padding: 3px 8px; font-size: 10px; font-weight: 700; }
  .btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .search-bar { display: flex; gap: 10px; margin-bottom: 16px; align-items: center; }
  .search-input { flex: 1; background: var(--surface); border: 1px solid var(--border); border-radius: 8px; padding: 9px 14px; font-size: 13px; color: var(--text); font-family: 'DM Sans', sans-serif; outline: none; }
  .search-input::placeholder { color: var(--text-muted); }
  .search-input:focus { border-color: var(--blue); }
  .filter-select { background: var(--surface); border: 1px solid var(--border); border-radius: 8px; padding: 9px 12px; font-size: 13px; color: var(--text); font-family: 'DM Sans', sans-serif; outline: none; cursor: pointer; min-width: 160px; }
  .parts-table { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; }
  .parts-table-header { display: grid; grid-template-columns: 2fr 1.2fr 1fr 0.8fr 0.8fr 0.8fr 1fr 90px; gap: 10px; padding: 11px 18px; background: var(--surface2); border-bottom: 1px solid var(--border); }
  .th { font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--text-muted); }
  .part-row { display: grid; grid-template-columns: 2fr 1.2fr 1fr 0.8fr 0.8fr 0.8fr 1fr 90px; gap: 10px; padding: 13px 18px; border-bottom: 1px solid var(--border); align-items: center; transition: background 0.12s; }
  .part-row:last-child { border-bottom: none; }
  .part-row:hover { background: var(--surface2); }
  .part-name { font-size: 13px; font-weight: 600; color: var(--text); }
  .td { font-size: 12px; color: var(--text-sub); }
  .price-cell { font-family: 'JetBrains Mono', monospace; font-size: 13px; font-weight: 600; }
  .price-cost { color: var(--text-muted); } .price-sell { color: var(--green); }
  .row-actions { display: flex; gap: 5px; }
  .cat-badge { padding: 3px 8px; border-radius: 4px; font-size: 10px; font-weight: 700; background: var(--blue-dim); color: var(--blue); border: 1px solid rgba(59,130,246,0.25); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 130px; display: block; }
  .add-part-card { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 24px; margin-bottom: 24px; }
  .add-part-title { font-size: 13px; font-weight: 700; color: var(--text); margin-bottom: 18px; display: flex; align-items: center; gap: 8px; }
  .form-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 12px; }
  .form-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 12px; }
  .field { display: flex; flex-direction: column; gap: 5px; }
  .field label { font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--text-muted); }
  .field input, .field select { background: var(--bg); border: 1px solid var(--border); border-radius: 7px; padding: 9px 12px; font-size: 13px; color: var(--text); font-family: 'DM Sans', sans-serif; outline: none; width: 100%; appearance: none; }
  .field input::placeholder { color: var(--text-muted); }
  .field input:focus, .field select:focus { border-color: var(--blue); }
  .field-hint { font-size: 10px; color: var(--text-muted); margin-top: 3px; }
  .form-actions { display: flex; justify-content: flex-end; gap: 10px; }
  .csv-template-card { background: var(--surface2); border: 1px solid var(--border); border-radius: 10px; padding: 16px 20px; display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
  .csv-template-title { font-size: 13px; font-weight: 600; color: var(--text); margin-bottom: 3px; }
  .csv-template-desc { font-size: 12px; color: var(--text-muted); }
  .csv-columns { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 10px; }
  .csv-col { background: var(--surface); border: 1px solid var(--border); border-radius: 5px; padding: 4px 10px; font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--text-sub); }
  .markup-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 24px; }
  .markup-card { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 22px; }
  .markup-card-title { font-size: 11px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 18px; display: flex; align-items: center; gap: 8px; }
  .markup-card-title::before { content: ''; display: block; width: 3px; height: 12px; border-radius: 2px; }
  .markup-card-title.best::before { background: var(--accent); } .markup-card-title.better::before { background: var(--blue); } .markup-card-title.good::before { background: var(--green); } .markup-card-title.global::before { background: var(--purple); }
  .markup-row { display: flex; align-items: center; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid var(--border); }
  .markup-row:last-child { border-bottom: none; }
  .markup-row-label { font-size: 13px; color: var(--text-sub); }
  .markup-row-input-wrap { display: flex; align-items: center; gap: 6px; }
  .markup-input { background: var(--bg); border: 1px solid var(--border); border-radius: 6px; padding: 6px 10px; font-size: 14px; font-weight: 700; color: var(--text); font-family: 'JetBrains Mono', monospace; outline: none; width: 70px; text-align: right; }
  .markup-input:focus { border-color: var(--accent); }
  .markup-unit { font-size: 12px; color: var(--text-muted); font-weight: 600; }
  .markup-preview-box { background: var(--surface2); border: 1px solid var(--border); border-radius: 10px; padding: 16px; margin-top: 14px; }
  .markup-preview-title { font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 12px; }
  .markup-example-row { display: flex; justify-content: space-between; align-items: center; padding: 7px 0; border-bottom: 1px solid var(--border); }
  .markup-example-row:last-child { border-bottom: none; }
  .markup-example-label { font-size: 12px; color: var(--text-muted); }
  .markup-example-val { font-family: 'JetBrains Mono', monospace; font-size: 13px; font-weight: 600; }
  .global-toggle { display: flex; align-items: center; justify-content: space-between; padding: 14px 0; border-bottom: 1px solid var(--border); margin-bottom: 4px; }
  .global-toggle-label { font-size: 13px; color: var(--text); font-weight: 500; }
  .global-toggle-desc { font-size: 11px; color: var(--text-muted); margin-top: 2px; }
  .toggle { width: 40px; height: 22px; border-radius: 11px; cursor: pointer; transition: background 0.2s; position: relative; flex-shrink: 0; }
  .toggle.on { background: var(--accent); } .toggle.off { background: var(--border); }
  .toggle-thumb { position: absolute; top: 3px; width: 16px; height: 16px; border-radius: 50%; background: #fff; transition: left 0.2s; }
  .toggle.on .toggle-thumb { left: 21px; } .toggle.off .toggle-thumb { left: 3px; }
  .visibility-card { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 22px; }
  .setup-banner { background: linear-gradient(135deg, rgba(245,166,35,0.08) 0%, rgba(59,130,246,0.06) 100%); border: 1px solid rgba(245,166,35,0.3); border-radius: 14px; padding: 24px 28px; margin-bottom: 28px; display: flex; align-items: flex-start; gap: 20px; }
  .setup-banner.complete { background: linear-gradient(135deg, rgba(34,197,94,0.08) 0%, rgba(34,197,94,0.04) 100%); border-color: rgba(34,197,94,0.3); }
  .setup-banner-icon { font-size: 32px; flex-shrink: 0; margin-top: 2px; }
  .setup-banner-content { flex: 1; }
  .setup-banner-title { font-family: 'Bebas Neue', sans-serif; font-size: 20px; letter-spacing: 2px; color: var(--text); margin-bottom: 6px; }
  .setup-banner-desc { font-size: 13px; color: var(--text-sub); line-height: 1.6; margin-bottom: 16px; max-width: 640px; }
  .setup-progress-wrap { display: flex; flex-direction: column; gap: 6px; }
  .setup-progress-top { display: flex; justify-content: space-between; align-items: center; }
  .setup-progress-label { font-size: 12px; font-weight: 700; color: var(--text-sub); }
  .setup-progress-pct { font-family: 'Bebas Neue', sans-serif; font-size: 18px; letter-spacing: 1px; }
  .setup-progress-bar { height: 6px; background: var(--border); border-radius: 3px; overflow: hidden; }
  .setup-progress-fill { height: 100%; border-radius: 3px; background: linear-gradient(90deg, var(--accent), var(--blue)); transition: width 0.4s ease; }
  .setup-progress-fill.complete { background: var(--green); }
  .setup-steps { display: flex; gap: 12px; margin-top: 14px; flex-wrap: wrap; }
  .setup-step { display: flex; align-items: center; gap: 7px; padding: 6px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
  .setup-step.done { background: var(--green-dim); color: var(--green); border: 1px solid rgba(34,197,94,0.3); }
  .setup-step.pending { background: var(--surface2); color: var(--text-muted); border: 1px solid var(--border); }
  .setup-step.active { background: var(--accent-dim); color: var(--accent); border: 1px solid rgba(245,166,35,0.3); }
  .live-badge { display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px; border-radius: 20px; font-size: 10px; font-weight: 700; background: var(--green-dim); color: var(--green); border: 1px solid rgba(34,197,94,0.3); letter-spacing: 0.3px; }
  .live-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--green); animation: pulse 1.5s infinite; }
  @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
  .match-table { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; margin-bottom: 16px; }
  .match-table-header { display: grid; grid-template-columns: 2fr 1.4fr 1.4fr 0.8fr 130px; gap: 12px; padding: 12px 18px; background: var(--surface2); border-bottom: 1px solid var(--border); }
  .match-row { display: grid; grid-template-columns: 2fr 1.4fr 1.4fr 0.8fr 130px; gap: 12px; padding: 16px 18px; border-bottom: 1px solid var(--border); align-items: center; transition: background 0.12s; }
  .match-row:last-child { border-bottom: none; }
  .match-row:hover { background: var(--surface2); }
  .match-oem-name { font-size: 13px; font-weight: 600; color: var(--text); }
  .match-oem-num { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--text-muted); margin-top: 2px; }
  .match-tier { display: flex; flex-direction: column; gap: 3px; }
  .match-tier-brand { font-size: 12px; font-weight: 600; color: var(--text-sub); }
  .match-tier-num { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--text-muted); }
  .match-tier-price { font-family: 'JetBrains Mono', monospace; font-size: 13px; font-weight: 700; margin-top: 2px; }
  .match-tier-price.better { color: var(--blue); } .match-tier-price.good { color: var(--green); }
  .match-tier-live { display: flex; align-items: center; gap: 5px; font-size: 10px; color: var(--text-muted); margin-top: 3px; }
  .match-status { display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px; border-radius: 20px; font-size: 10px; font-weight: 700; letter-spacing: 0.3px; }
  .match-status.pending { background: var(--accent-dim); color: var(--accent); border: 1px solid rgba(245,166,35,0.3); }
  .match-status.approved { background: var(--green-dim); color: var(--green); border: 1px solid rgba(34,197,94,0.3); }
  .match-status.rejected { background: var(--red-dim); color: var(--red); border: 1px solid rgba(239,68,68,0.3); }
  .match-actions { display: flex; gap: 6px; align-items: center; }
  .how-it-works { background: var(--surface2); border: 1px solid var(--border); border-radius: 10px; padding: 18px 20px; margin-bottom: 24px; }
  .how-it-works-title { font-size: 11px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 14px; display: flex; align-items: center; gap: 8px; }
  .how-it-works-title::before { content: ''; display: block; width: 3px; height: 12px; background: var(--blue); border-radius: 2px; }
  .how-steps { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
  .how-step { display: flex; flex-direction: column; gap: 6px; }
  .how-step-num { font-family: 'Bebas Neue', sans-serif; font-size: 28px; color: var(--accent); letter-spacing: 1px; line-height: 1; }
  .how-step-title { font-size: 12px; font-weight: 700; color: var(--text); }
  .how-step-desc { font-size: 11px; color: var(--text-muted); line-height: 1.5; }
  .empty-state { text-align: center; padding: 48px 24px; color: var(--text-muted); }
  .empty-state-icon { font-size: 40px; margin-bottom: 12px; }
  .empty-state-title { font-family: 'Bebas Neue', sans-serif; font-size: 20px; letter-spacing: 2px; color: var(--text-sub); margin-bottom: 8px; }
  .toast { position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); background: var(--surface); border: 1px solid var(--green); border-radius: 8px; padding: 11px 20px; font-size: 13px; font-weight: 600; color: var(--green); z-index: 999; box-shadow: 0 8px 24px rgba(0,0,0,0.4); display: flex; align-items: center; gap: 8px; }
  .toast.warn { border-color: var(--red); color: var(--red); }
  @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
`;

const CATEGORIES = [
  "Brake Pads — Front","Brake Pads — Rear","Rotors — Front","Rotors — Rear",
  "Oil & Filter","Air Filter","Cabin Air Filter","Spark Plugs","Battery",
  "Wiper Blades","Shocks & Struts","Timing Belt","Coolant Flush","Transmission Fluid",
];

const sellPrice  = (cost, markup) => (cost * (1 + markup / 100)).toFixed(2);
const marginDollar = (cost, markup) => (cost * markup / 100).toFixed(2);

const initials = (name) => name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);

export default function PartsManager({ currentUser, onLogout, onNavigate }) {
  const [activeNav,    setActiveNav]    = useState("setup");
  const [parts,        setParts]        = useState([]);
  const [matches,      setMatches]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [search,       setSearch]       = useState("");
  const [catFilter,    setCatFilter]    = useState("All");
  const [toast,        setToast]        = useState(null);
  const [globalMarkup, setGlobalMarkup] = useState(false);
  const [globalRate,   setGlobalRate]   = useState(30);
  const [markups,      setMarkups]      = useState({ best: 30, better: 20, good: 10 });
  const [markupSaved,  setMarkupSaved]  = useState(false);
  const [newPart,      setNewPart]      = useState({ name: "", partNum: "", category: CATEGORIES[0], cost: "", markup: 30 });
  const [showAddForm,  setShowAddForm]  = useState(false);
  const [storeName,    setStoreName]    = useState("");

  const showToast = (msg, type = "success") => {
    setToast({ message: msg, type });
    setTimeout(() => setToast(null), 3200);
  };

  // ── Load all data on mount ────────────────────────────────
  useEffect(() => {
    if (currentUser?.storeId) loadAll();
  }, [currentUser?.storeId]); // eslint-disable-line

  const loadAll = async () => {
    setLoading(true);
    const sid = currentUser.storeId;
    try {
      // Store info (name + markup settings)
      const storeSnap = await getDoc(doc(db, "stores", sid));
      if (storeSnap.exists()) {
        const sd = storeSnap.data();
        setStoreName(sd.name || "");
        if (sd.markupSettings) {
          setMarkups(sd.markupSettings.tiers || { best: 30, better: 20, good: 10 });
          setGlobalMarkup(sd.markupSettings.globalEnabled || false);
          setGlobalRate(sd.markupSettings.globalRate || 30);
        }
      }
      // Parts catalog
      const partsSnap = await getDocs(collection(db, "stores", sid, "parts"));
      setParts(partsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      // Tier matches
      const matchSnap = await getDocs(collection(db, "stores", sid, "matches"));
      setMatches(matchSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error("PartsManager loadAll:", e);
      showToast("Error loading data", "warn");
    }
    setLoading(false);
  };

  // ── Parts CRUD ────────────────────────────────────────────
  const addPart = async () => {
    if (!newPart.name || !newPart.partNum || !newPart.cost) {
      showToast("Please fill in all required fields", "warn"); return;
    }
    setSaving(true);
    try {
      const partData = {
        name:      newPart.name,
        partNum:   newPart.partNum,
        category:  newPart.category,
        cost:      parseFloat(newPart.cost),
        markup:    globalMarkup ? globalRate : parseInt(newPart.markup),
        matched:   false,
        createdAt: serverTimestamp(),
        storeId:   currentUser.storeId,
      };
      const ref = await addDoc(collection(db, "stores", currentUser.storeId, "parts"), partData);
      setParts(p => [...p, { ...partData, id: ref.id }]);
      setNewPart({ name: "", partNum: "", category: CATEGORIES[0], cost: "", markup: 30 });
      setShowAddForm(false);
      showToast("Part added — it will appear in tier matching for review");
    } catch (e) {
      console.error("addPart:", e);
      showToast("Failed to save part", "warn");
    }
    setSaving(false);
  };

  const deletePart = async (id) => {
    try {
      await deleteDoc(doc(db, "stores", currentUser.storeId, "parts", id));
      setParts(p => p.filter(x => x.id !== id));
      showToast("Part removed", "warn");
    } catch (e) {
      console.error("deletePart:", e);
      showToast("Failed to delete part", "warn");
    }
  };

  // ── Match approve / reject ────────────────────────────────
  const approveMatch = async (id) => {
    try {
      await updateDoc(doc(db, "stores", currentUser.storeId, "matches", id), { status: "approved" });
      const match = matches.find(m => m.id === id);
      if (match?.oemNum) {
        // Mark the corresponding part as matched
        const part = parts.find(p => p.partNum === match.oemNum);
        if (part) {
          await updateDoc(doc(db, "stores", currentUser.storeId, "parts", part.id), { matched: true });
          setParts(p => p.map(x => x.id === part.id ? { ...x, matched: true } : x));
        }
      }
      setMatches(m => m.map(x => x.id === id ? { ...x, status: "approved" } : x));
      showToast("Match approved — live for advisors immediately");
    } catch (e) {
      console.error("approveMatch:", e);
      showToast("Failed to update match", "warn");
    }
  };

  const rejectMatch = async (id) => {
    try {
      await updateDoc(doc(db, "stores", currentUser.storeId, "matches", id), { status: "rejected" });
      setMatches(m => m.map(x => x.id === id ? { ...x, status: "rejected" } : x));
      showToast("Match rejected — you can re-approve anytime", "warn");
    } catch (e) {
      console.error("rejectMatch:", e);
      showToast("Failed to update match", "warn");
    }
  };

  // ── Save markup settings ──────────────────────────────────
  const saveMarkupSettings = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, "stores", currentUser.storeId), {
        markupSettings: {
          tiers:         markups,
          globalEnabled: globalMarkup,
          globalRate:    globalRate,
        },
        updatedAt: serverTimestamp(),
      });
      setMarkupSaved(true);
      setTimeout(() => setMarkupSaved(false), 2500);
      showToast("Markup settings saved");
    } catch (e) {
      console.error("saveMarkupSettings:", e);
      showToast("Failed to save markup settings", "warn");
    }
    setSaving(false);
  };

  // ── Derived stats ─────────────────────────────────────────
  const filteredParts   = parts.filter(p => {
    const s = p.name?.toLowerCase().includes(search.toLowerCase()) || p.partNum?.toLowerCase().includes(search.toLowerCase());
    const c = catFilter === "All" || p.category === catFilter;
    return s && c;
  });
  const totalParts      = parts.length;
  const matchedParts    = parts.filter(p => p.matched).length;
  const pendingMatches  = matches.filter(m => m.status === "pending").length;
  const approvedMatches = matches.filter(m => m.status === "approved").length;
  const setupPct        = totalParts > 0 ? Math.round((matchedParts / totalParts) * 100) : 0;
  const setupComplete   = totalParts > 0 && setupPct === 100;

  const setupSteps = [
    { label: "Upload OEM Catalog",    done: totalParts > 0 },
    { label: "Set Markup Rules",      done: true },
    { label: "Approve Tier Matches",  done: pendingMatches === 0 && matches.length > 0 },
    { label: "Go Live",               done: setupComplete },
  ];

  const ini = initials(currentUser?.name || "PM");

  if (loading) {
    return (
      <>
        <style>{styles}</style>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", color:"var(--text-muted)", fontFamily:"'DM Sans',sans-serif", background:"#0E1117" }}>
          Loading parts catalog...
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>

      <header className="header">
        <div className="logo">
          <div className="logo-icon">L</div>
          <div className="logo-text">LANE<span>IQ</span></div>
        </div>
        <span className="header-badge">🔧 Parts Manager</span>
        <div className="header-right">
          <div className="header-store">
            <strong>{storeName || "Parts Dashboard"}</strong> &nbsp;·&nbsp; Parts Manager
          </div>
          <div className="avatar" title={currentUser?.name}>{ini}</div>
          {onLogout && (
            <button onClick={onLogout} style={{ background:"transparent", border:"1px solid var(--border)", borderRadius:6, padding:"6px 12px", fontSize:12, color:"var(--text-muted)", cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
              Sign Out
            </button>
          )}
        </div>
      </header>

      <div className="layout">
        <aside className="sidebar">
          <div className="sidebar-label">Setup & Management</div>
          {[
            { id: "setup",    icon: "🚀", label: "Setup Progress",   badge: setupComplete ? "✓" : totalParts > 0 ? `${setupPct}%` : null, badgeClass: setupComplete ? "complete" : "" },
            { id: "catalog",  icon: "📦", label: "OEM Catalog" },
            { id: "markup",   icon: "💰", label: "Markup Settings" },
            { id: "matching", icon: "🔗", label: "Tier Matching",    badge: pendingMatches > 0 ? pendingMatches : null },
          ].map(item => (
            <div key={item.id} className={`nav-item ${activeNav === item.id ? "active" : ""}`} onClick={() => setActiveNav(item.id)}>
              <span className="nav-icon">{item.icon}</span>
              {item.label}
              {item.badge && <span className={`nav-badge ${item.badgeClass || ""}`}>{item.badge}</span>}
            </div>
          ))}
          <div className="sidebar-label">Quick Access</div>
          {[
            { id: "advisor",  icon: "🔍", label: "Advisor View" },
            { id: "reports",  icon: "📈", label: "Reports" },
          ].map(item => (
            <div key={item.id} className="nav-item" onClick={() => onNavigate?.(item.id)}>
              <span className="nav-icon">{item.icon}</span>
              {item.label}
            </div>
          ))}
        </aside>

        <main className="main">

          {/* ── SETUP PROGRESS ── */}
          {activeNav === "setup" && (
            <>
              <div className="page-head">
                <div className="page-head-left">
                  <h1>SETUP & ONBOARDING</h1>
                  <p>Complete these steps once — then LaneIQ runs automatically for your advisors</p>
                </div>
              </div>
              <div className={`setup-banner ${setupComplete ? "complete" : ""}`}>
                <div className="setup-banner-icon">{setupComplete ? "🎉" : "⚡"}</div>
                <div className="setup-banner-content">
                  <div className="setup-banner-title">
                    {setupComplete ? "SETUP COMPLETE — ADVISORS ARE LIVE" : "COMPLETE YOUR STORE SETUP"}
                  </div>
                  <div className="setup-banner-desc">
                    {setupComplete
                      ? "All parts are matched and your markup rules are set. Advisors are seeing live Good · Better · Best pricing on every write-up."
                      : "This is a one-time setup. Once complete, LaneIQ automatically shows live pricing to advisors on every lookup — no daily maintenance required."}
                  </div>
                  <div className="setup-progress-wrap">
                    <div className="setup-progress-top">
                      <span className="setup-progress-label">{matchedParts} of {totalParts} parts matched</span>
                      <span className="setup-progress-pct" style={{ color: setupComplete ? "var(--green)" : "var(--accent)" }}>{setupPct}%</span>
                    </div>
                    <div className="setup-progress-bar">
                      <div className={`setup-progress-fill ${setupComplete ? "complete" : ""}`} style={{ width: `${setupPct}%` }} />
                    </div>
                  </div>
                  <div className="setup-steps">
                    {setupSteps.map((s, i) => (
                      <div key={s.label} className={`setup-step ${s.done ? "done" : i === setupSteps.findIndex(x => !x.done) ? "active" : "pending"}`}>
                        {s.done ? "✓" : i === setupSteps.findIndex(x => !x.done) ? "→" : "○"} {s.label}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="how-it-works">
                <div className="how-it-works-title">How Tier Matching Works</div>
                <div className="how-steps">
                  {[
                    { num: "01", title: "You upload your OEM catalog",   desc: "Add your parts with cost and part numbers. This becomes the Best tier." },
                    { num: "02", title: "System suggests matches",        desc: "PartsTech's engine finds premium and value aftermarket equivalents for each OEM part." },
                    { num: "03", title: "You approve once",               desc: "Review suggestions and click approve. An afternoon of work — not ongoing." },
                    { num: "04", title: "Advisors see live pricing",      desc: "Every search pulls real-time prices and availability. Matches are permanent — pricing is always fresh." },
                  ].map(s => (
                    <div key={s.num} className="how-step">
                      <div className="how-step-num">{s.num}</div>
                      <div className="how-step-title">{s.title}</div>
                      <div className="how-step-desc">{s.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="stat-strip">
                {[
                  { label: "OEM Parts Loaded",   val: totalParts,    sub: "in catalog",         color: totalParts > 0 ? "green" : "red",  action: () => setActiveNav("catalog"),  actionLabel: totalParts > 0 ? "Manage Catalog" : "Upload Parts" },
                  { label: "Markup Rules Set",    val: "✓",           sub: "configured",         color: "green",                           action: () => setActiveNav("markup"),   actionLabel: "Review Settings" },
                  { label: "Matches Pending",     val: pendingMatches,sub: "need review",        color: pendingMatches > 0 ? "amber":"green",action: () => setActiveNav("matching"),actionLabel: pendingMatches > 0 ? "Review Now" : "View Matches" },
                  { label: "Parts Live",          val: matchedParts,  sub: "visible to advisors",color: matchedParts > 0 ? "green" : "red", action: null, actionLabel: null },
                ].map(card => (
                  <div key={card.label} className={`stat-card ${card.color === "green" ? "complete" : ""}`}>
                    <div className="stat-label">{card.label}</div>
                    <div className={`stat-value ${card.color}`}>{card.val}</div>
                    <div className="stat-sub">{card.sub}</div>
                    {card.action && <button className="btn btn-ghost btn-sm" style={{ marginTop:10, width:"100%" }} onClick={card.action}>{card.actionLabel} →</button>}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── OEM CATALOG ── */}
          {activeNav === "catalog" && (
            <>
              <div className="page-head">
                <div className="page-head-left">
                  <h1>OEM CATALOG</h1>
                  <p>Your dealership's OEM parts — the Best tier in every advisor lookup</p>
                </div>
                <div className="page-head-actions">
                  <button className="btn btn-ghost">⬇ Download Template</button>
                  <button className="btn btn-ghost">⬆ Import CSV</button>
                  <button className="btn btn-blue" onClick={() => setShowAddForm(!showAddForm)}>+ Add Part</button>
                </div>
              </div>

              <div className="stat-strip">
                <div className="stat-card highlight"><div className="stat-label">Total Parts</div><div className="stat-value blue">{parts.length}</div><div className="stat-sub">In catalog</div></div>
                <div className="stat-card complete"><div className="stat-label">Matched & Live</div><div className="stat-value green">{parts.filter(p => p.matched).length}</div><div className="stat-sub">Visible to advisors</div></div>
                <div className="stat-card"><div className="stat-label">Awaiting Match</div><div className="stat-value" style={{ color: parts.filter(p => !p.matched).length > 0 ? "var(--accent)" : "var(--green)" }}>{parts.filter(p => !p.matched).length}</div><div className="stat-sub">Not yet visible</div></div>
                <div className="stat-card"><div className="stat-label">Categories</div><div className="stat-value amber">{[...new Set(parts.map(p => p.category))].length}</div><div className="stat-sub">Repair types covered</div></div>
              </div>

              <div className="csv-template-card">
                <div>
                  <div className="csv-template-title">📄 Bulk Import via CSV</div>
                  <div className="csv-template-desc">Upload hundreds of parts at once. Download the template for the correct column format.</div>
                  <div className="csv-columns">
                    {["part_name","part_number","category","cost","markup_pct"].map(c => <span key={c} className="csv-col">{c}</span>)}
                  </div>
                </div>
                <button className="btn btn-ghost">⬇ Download Template</button>
              </div>

              {showAddForm && (
                <div className="add-part-card">
                  <div className="add-part-title">➕ Add New OEM Part</div>
                  <div className="form-grid">
                    <div className="field" style={{ gridColumn: "span 2" }}>
                      <label>Part Name *</label>
                      <input placeholder="e.g. Front Disc Brake Pad Set" value={newPart.name} onChange={e => setNewPart(p => ({ ...p, name: e.target.value }))} />
                    </div>
                    <div className="field">
                      <label>OEM Part Number *</label>
                      <input placeholder="e.g. 04465-06120" style={{ fontFamily:"'JetBrains Mono',monospace" }} value={newPart.partNum} onChange={e => setNewPart(p => ({ ...p, partNum: e.target.value }))} />
                    </div>
                    <div className="field">
                      <label>Category</label>
                      <select value={newPart.category} onChange={e => setNewPart(p => ({ ...p, category: e.target.value }))}>
                        {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="form-grid-3">
                    <div className="field">
                      <label>Your Cost ($) *</label>
                      <input type="number" placeholder="0.00" min="0" step="0.01" value={newPart.cost} onChange={e => setNewPart(p => ({ ...p, cost: e.target.value }))} />
                      <div className="field-hint">Never shown to advisors</div>
                    </div>
                    <div className="field">
                      <label>Markup % (Best tier)</label>
                      <input type="number" placeholder="30" min="0" max="200" value={globalMarkup ? globalRate : newPart.markup} disabled={globalMarkup} onChange={e => setNewPart(p => ({ ...p, markup: parseInt(e.target.value) || 0 }))} />
                      <div className="field-hint">{globalMarkup ? "Global markup applied" : "Override per-part"}</div>
                    </div>
                    <div className="field">
                      <label>Selling Price Preview</label>
                      <input readOnly value={newPart.cost ? `$${sellPrice(parseFloat(newPart.cost), globalMarkup ? globalRate : newPart.markup)}` : "—"} style={{ color:"var(--green)", fontFamily:"'JetBrains Mono',monospace", fontWeight:700, background:"var(--surface2)" }} />
                      <div className="field-hint" style={{ color:"var(--accent)" }}>
                        {newPart.cost ? `Margin: $${marginDollar(parseFloat(newPart.cost), globalMarkup ? globalRate : newPart.markup)}` : "Enter cost to preview"}
                      </div>
                    </div>
                  </div>
                  <div className="form-actions">
                    <button className="btn btn-ghost" onClick={() => setShowAddForm(false)}>Cancel</button>
                    <button className="btn btn-blue" onClick={addPart} disabled={saving}>{saving ? "Saving..." : "Save Part"}</button>
                  </div>
                </div>
              )}

              <div className="search-bar">
                <input className="search-input" placeholder="Search by part name or number..." value={search} onChange={e => setSearch(e.target.value)} />
                <select className="filter-select" value={catFilter} onChange={e => setCatFilter(e.target.value)}>
                  <option>All</option>
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>

              <div className="section-bar">
                <div className="section-title">OEM Parts ({filteredParts.length})</div>
              </div>

              {filteredParts.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">📦</div>
                  <div className="empty-state-title">No Parts Yet</div>
                  <div style={{ fontSize:13, marginBottom:16 }}>Add your first OEM part to get started</div>
                  <button className="btn btn-blue" onClick={() => setShowAddForm(true)}>+ Add First Part</button>
                </div>
              ) : (
                <div className="parts-table">
                  <div className="parts-table-header">
                    <div className="th">Part Name</div><div className="th">Category</div><div className="th">Part #</div>
                    <div className="th">Cost</div><div className="th">Markup</div><div className="th">Sell Price</div>
                    <div className="th">Status</div><div className="th">Actions</div>
                  </div>
                  {filteredParts.map(p => (
                    <div key={p.id} className="part-row">
                      <div><div className="part-name">{p.name}</div></div>
                      <div><span className="cat-badge">{p.category}</span></div>
                      <div className="td" style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:11 }}>{p.partNum}</div>
                      <div className="price-cell price-cost">${p.cost?.toFixed(2)}</div>
                      <div className="price-cell" style={{ color:"var(--accent)" }}>{globalMarkup ? globalRate : p.markup}%</div>
                      <div className="price-cell price-sell">${sellPrice(p.cost, globalMarkup ? globalRate : p.markup)}</div>
                      <div>{p.matched ? <span className="match-status approved">✓ Live</span> : <span className="match-status pending">⏳ Needs Match</span>}</div>
                      <div className="row-actions">
                        <button className="btn btn-danger btn-xs" onClick={() => deletePart(p.id)}>Del</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── MARKUP SETTINGS ── */}
          {activeNav === "markup" && (
            <>
              <div className="page-head">
                <div className="page-head-left">
                  <h1>MARKUP SETTINGS</h1>
                  <p>Set your margin rules once — they apply automatically to every part lookup</p>
                </div>
                <button className="btn btn-primary" onClick={saveMarkupSettings} disabled={saving}>
                  {saving ? "Saving..." : markupSaved ? "✓ Saved" : "Save All Settings"}
                </button>
              </div>

              <div className="markup-card" style={{ marginBottom: 16 }}>
                <div className="markup-card-title global">Global Markup Override</div>
                <div className="global-toggle">
                  <div>
                    <div className="global-toggle-label">Apply one markup rate to all tiers</div>
                    <div className="global-toggle-desc">When enabled, this single rate overrides all individual tier settings</div>
                  </div>
                  <div className={`toggle ${globalMarkup ? "on" : "off"}`} onClick={() => setGlobalMarkup(!globalMarkup)}>
                    <div className="toggle-thumb" />
                  </div>
                </div>
                {globalMarkup && (
                  <div className="markup-row" style={{ paddingTop: 14 }}>
                    <div className="markup-row-label" style={{ fontWeight:600, color:"var(--text)" }}>Global Markup Rate</div>
                    <div className="markup-row-input-wrap">
                      <input className="markup-input" type="number" min="0" max="200" value={globalRate} onChange={e => setGlobalRate(parseInt(e.target.value) || 0)} />
                      <span className="markup-unit">%</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="markup-grid">
                {[
                  { key:"best",   label:"Best Tier (OEM)",                 cls:"best",   exCost:100, color:"var(--accent)" },
                  { key:"better", label:"Better Tier (Premium Aftermarket)",cls:"better", exCost:65,  color:"var(--blue)" },
                  { key:"good",   label:"Good Tier (Value Aftermarket)",    cls:"good",   exCost:39,  color:"var(--green)" },
                ].map(tier => (
                  <div key={tier.key} className="markup-card">
                    <div className={`markup-card-title ${tier.cls}`}>{tier.label}</div>
                    <div className="markup-row">
                      <div className="markup-row-label">Markup Percentage</div>
                      <div className="markup-row-input-wrap">
                        <input className="markup-input" type="number" min="0" max="200"
                          value={globalMarkup ? globalRate : markups[tier.key]}
                          disabled={globalMarkup}
                          onChange={e => setMarkups(m => ({ ...m, [tier.key]: parseInt(e.target.value) || 0 }))} />
                        <span className="markup-unit">%</span>
                      </div>
                    </div>
                    <div className="markup-preview-box">
                      <div className="markup-preview-title">Live Example — ${tier.exCost}.00 cost</div>
                      {[
                        { label:"Your Cost",    val:`$${tier.exCost}.00`, color:"var(--text-muted)" },
                        { label:"Markup",       val:`$${(tier.exCost*(globalMarkup?globalRate:markups[tier.key])/100).toFixed(2)}`, color:"var(--accent)" },
                        { label:"Advisor Sees", val:`$${(tier.exCost*(1+(globalMarkup?globalRate:markups[tier.key])/100)).toFixed(2)}`, color:tier.color },
                      ].map(row => (
                        <div key={row.label} className="markup-example-row">
                          <span className="markup-example-label">{row.label}</span>
                          <span className="markup-example-val" style={{ color:row.color }}>{row.val}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                <div className="visibility-card">
                  <div className="markup-card-title global">👁 Visibility Rules</div>
                  {[
                    { label:"Advisors see cost",         val:"Never",  color:"var(--red)" },
                    { label:"Advisors see markup %",      val:"Never",  color:"var(--red)" },
                    { label:"Advisors see selling price", val:"Always", color:"var(--green)" },
                    { label:"Advisors see margin $",      val:"Never",  color:"var(--red)" },
                    { label:"Service managers see all",   val:"Always", color:"var(--green)" },
                    { label:"Parts manager sees all",     val:"Always", color:"var(--green)" },
                  ].map(row => (
                    <div key={row.label} className="markup-example-row" style={{ padding:"9px 0" }}>
                      <span style={{ fontSize:13, color:"var(--text-sub)" }}>{row.label}</span>
                      <span style={{ fontSize:12, fontWeight:700, color:row.color }}>{row.val}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ── TIER MATCHING ── */}
          {activeNav === "matching" && (
            <>
              <div className="page-head">
                <div className="page-head-left">
                  <h1>TIER MATCHING</h1>
                  <p>One-time setup — approve these once and they run automatically forever</p>
                </div>
              </div>

              {matches.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">🔗</div>
                  <div className="empty-state-title">No Matches Yet</div>
                  <div style={{ fontSize:13, color:"var(--text-muted)", marginBottom:8 }}>
                    Tier matches will appear here once your catalog is connected to PartsTech.
                  </div>
                  <div style={{ fontSize:12, color:"var(--text-muted)" }}>
                    Add OEM parts to your catalog first, then PartsTech will suggest Better and Good tier alternatives.
                  </div>
                </div>
              ) : (
                <>
                  {pendingMatches > 0 ? (
                    <div className="setup-banner" style={{ marginBottom:24 }}>
                      <div className="setup-banner-icon">🔗</div>
                      <div className="setup-banner-content">
                        <div className="setup-banner-title">ONE-TIME APPROVAL — NOT A DAILY TASK</div>
                        <div className="setup-banner-desc">
                          These matches were suggested automatically. Review each one, approve what looks right.
                          <strong style={{ color:"var(--accent)" }}> Once approved, pricing updates live on every advisor search.</strong>
                        </div>
                        <div className="setup-progress-wrap">
                          <div className="setup-progress-top">
                            <span className="setup-progress-label">{approvedMatches} of {matches.length} matches approved</span>
                            <span className="setup-progress-pct" style={{ color:"var(--accent)" }}>{Math.round((approvedMatches/matches.length)*100)}%</span>
                          </div>
                          <div className="setup-progress-bar">
                            <div className="setup-progress-fill" style={{ width:`${Math.round((approvedMatches/matches.length)*100)}%` }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="setup-banner complete" style={{ marginBottom:24 }}>
                      <div className="setup-banner-icon">🎉</div>
                      <div className="setup-banner-content">
                        <div className="setup-banner-title">ALL MATCHES APPROVED — ADVISORS ARE LIVE</div>
                        <div className="setup-banner-desc">Every part is matched and active. Come back only if you need to swap a match.</div>
                      </div>
                    </div>
                  )}

                  <div className="stat-strip">
                    <div className="stat-card"><div className="stat-label">Pending Review</div><div className="stat-value" style={{ color:pendingMatches>0?"var(--accent)":"var(--green)" }}>{pendingMatches}</div><div className="stat-sub">{pendingMatches>0?"Need your approval":"All reviewed"}</div></div>
                    <div className="stat-card complete"><div className="stat-label">Approved & Live</div><div className="stat-value green">{approvedMatches}</div><div className="stat-sub">Advisors can see these</div></div>
                    <div className="stat-card"><div className="stat-label">Rejected</div><div className="stat-value" style={{ color:"var(--text-muted)" }}>{matches.filter(m=>m.status==="rejected").length}</div><div className="stat-sub">Re-approve anytime</div></div>
                    <div className="stat-card"><div className="stat-label">Live Data Source</div><div style={{ marginTop:6 }}><span className="live-badge"><span className="live-dot" />PartsTech API</span></div><div className="stat-sub">Price & stock always fresh</div></div>
                  </div>

                  <div className="match-table">
                    <div className="match-table-header">
                      <div className="th">OEM Part (Best Tier)</div>
                      <div className="th">Better Tier Match</div>
                      <div className="th">Good Tier Match</div>
                      <div className="th">Status</div>
                      <div className="th">Actions</div>
                    </div>
                    {matches.map(m => (
                      <div key={m.id} className="match-row">
                        <div>
                          <div className="match-oem-name">{m.oemName}</div>
                          <div className="match-oem-num">OEM #{m.oemNum}</div>
                        </div>
                        <div className="match-tier">
                          <div className="match-tier-brand">{m.betterBrand}</div>
                          <div className="match-tier-num">#{m.betterNum}</div>
                          <div className="match-tier-price better">${m.betterPrice}</div>
                        </div>
                        <div className="match-tier">
                          <div className="match-tier-brand">{m.goodBrand}</div>
                          <div className="match-tier-num">#{m.goodNum}</div>
                          <div className="match-tier-price good">${m.goodPrice}</div>
                        </div>
                        <div>
                          <span className={`match-status ${m.status}`}>
                            {m.status==="pending"?"⏳ Pending":m.status==="approved"?"✓ Live":"✕ Rejected"}
                          </span>
                        </div>
                        <div className="match-actions">
                          {m.status==="pending" && (
                            <><button className="btn btn-green btn-xs" onClick={() => approveMatch(m.id)}>✓ Approve</button>
                            <button className="btn btn-danger btn-xs" onClick={() => rejectMatch(m.id)}>✕ Reject</button></>
                          )}
                          {m.status==="approved" && <button className="btn btn-ghost btn-xs" onClick={() => rejectMatch(m.id)}>Swap Match</button>}
                          {m.status==="rejected" && <button className="btn btn-ghost btn-xs" onClick={() => approveMatch(m.id)}>Re-approve</button>}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}

        </main>
      </div>

      {toast && <div className={`toast ${toast.type==="warn"?"warn":""}`}>{toast.type==="warn"?"⚠":"✓"} {toast.message}</div>}
    </>
  );
}
